import { Component, Input, ViewChild, EventEmitter, Output, OnInit } from '@angular/core';
import { TransactionData, TransactionActionCommand, TransactionUpdateResult, DeclineRecalculationResultCode } from '../transaction.model';
import { TransactionListUpdateDialogComponent } from '../transaction-list-update-dialog/transaction-list-update-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { TransactionService } from '../transaction.service';
import { Subscription } from 'rxjs';
import { MessageService, TransactionStatus, TransactionType, parseNumber, SimpleDialogService } from 'common';
import { UserPermissionItem } from '../../user/user-permission/user-permission.service';
import { AccountService } from '../../account/account.service';
import { ActivatedRoute, Params, Router, UrlSerializer } from '@angular/router';
import { FailureReasonData } from '../../admin/failure-reason/failure-reason.model';
import { TransactionDeclinedDialogComponent } from '../transaction-declined-dialog/transaction-declined-dialog.component';
import { getManualReversalTooOldTransactionDialogData } from '../transaction-details/transaction-details.data';

@Component({
    selector: 'ifb-transaction-edit-menu',
    templateUrl: './transaction-edit-menu.component.html',
    styleUrls: ['./transaction-edit-menu.component.scss'],
    standalone: false
})
export class TransactionEditMenuComponent implements OnInit {

  private saveSub: Subscription;

  readonly TransactionStatus = TransactionStatus;
  readonly TransactionType = TransactionType;

  constructor(
    private transactionService: TransactionService,
    private accountService: AccountService,
    private dialog: MatDialog,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private router: Router,
    private serializer: UrlSerializer,
    private simpleDialogService: SimpleDialogService
  ) {}

  @Input()
  tx: TransactionData;

  @Input()
  failureReasons: FailureReasonData;

  @Input()
  canEditSubTransactions: boolean;

  @Input()
  CanUserMoveTransactionToStatusGrantedPermissions: UserPermissionItem[];

  @Input()
  expandedDetail: boolean;

  @Input()
  backToListUrl: string;

  @Output()
  statusChanged = new EventEmitter<any>();

  @Output()
  expandedDetailChanged = new EventEmitter<any>();

  @ViewChild(TransactionListUpdateDialogComponent) transactionListUpdateDialog;

  transactionDetailsQueryParams: Params;

  ngOnInit(): void {
    this.transactionDetailsQueryParams = { backTo: this.backToListUrl };
  }

  updateDialog(status: TransactionStatus) {
    this.transactionService.get(this.tx.id).subscribe(tx => {
      this._updateDialog(tx, status);
    });
  }
  _updateDialog(tx: TransactionData, status: TransactionStatus) {
    TransactionListUpdateDialogComponent.show(
      this.dialog,
      tx.notes,
      tx.loanNumber,
      status,
      tx.nextStatus,
      this.failureReasons,
      tx.status,
      tx.type,
      tx.drawRequirements,
      tx.customerDrawGeographicalPosition?.region,
      tx.businessAddress?.state,
      tx.failureReasons,
      tx.competitors
    )
      .subscribe(
        data => {
          if (!data)
            return;
          const updateCommand: TransactionActionCommand = {
            id: tx.id,
            loanId: tx.accountId,
            status: status,
            nextStatus: data.nextStatus,
            transferMethod: tx.transferMethod,
            transferSource: tx.transferSource,
            notes: [data.note],
            failureReasons: data.reasons,
            date: tx.date,
            processDate: tx.processDate,
            underwritingDate: tx.underwritingDate,
            financeDate: tx.financeDate,
            completeDate: tx.completeDate,
            cancellationDate: tx.cancellationDate,
            declinedDate: this.tx.declinedDate,
            competitors: data?.competitors,
            drawRequirementsHash: tx.drawRequirementsHash
          };
          this.saveSub = this.transactionService
            .update(updateCommand)
            .subscribe({
              next: (res) => this.saveSuccessHandler(res),
              error: (err) => this.saveErrorHandler(err),
            });
        });
  }

  private handleResultDialog(result: TransactionUpdateResult): void {
    if (result.declineRecalculationResultCode === DeclineRecalculationResultCode.TransactionProcessedOverXDaysAgo) {
      this.simpleDialogService.openSimpleDialog(getManualReversalTooOldTransactionDialogData(result?.currentLoan?.product?.reversibleDaysPeriod));
      return;
    }

    if (result.declineRecalculationResultCode === DeclineRecalculationResultCode.Success) {
      TransactionDeclinedDialogComponent.show(this.dialog, result).subscribe();
    }
  }

  private saveErrorHandler(error: any): void {
    this.saveSubClear();
    this.messageService.error(error);
    if (error?.status === 409) {
      this.statusChanged.emit();
    }
  }

  private saveSubClear(): void {
    if (this.saveSub)
      this.saveSub.unsubscribe();
    this.saveSub = null;
  }

  private saveSuccessHandler(result: TransactionUpdateResult): void {
    this.handleResultDialog(result);
    if (this.route.snapshot.params.uniqueId)
      this.accountService.getByUniqueId(this.route.snapshot.params.uniqueId).subscribe(data => {
        this.saveSuccessCompleted(data);
      });
    else {
      const loanId = parseNumber(this.route.snapshot.params.id) || this.tx.accountId;
      this.accountService.getWithRenewal(loanId).subscribe(data => {
        this.saveSuccessCompleted(data);
      });
    }
  }

  saveSuccessCompleted(data): void {
    this.accountService.onCurrentLoanChanged.next(data);
    this.saveSubClear();
    this.statusChanged.emit();
  }

  transactionDetailsLink(tx: TransactionData): string {
    return `/account/${tx.accountId}/transaction/${tx.id}`;
  }

  transactionDetailsLinkAdmin(tx: TransactionData): string {
    return `/account/${tx.accountId}/transaction/${tx.id}${this.canEditSubTransactions ? '/admin' : ''}`;
  }

  onExpandedDetailChanged(): void {
    this.expandedDetailChanged.emit();
  }

  viewNewTab(tx): void {
    const tree = this.router.createUrlTree([`/account/${tx.accountId}/transaction/${tx.id}/view-mode`], { queryParams: this.transactionDetailsQueryParams });
    window.open(this.serializer.serialize(tree));
  }
}
